import { CalendarIcon, DollarSignIcon, TrendingUpIcon, FlaskConical, Eye } from "lucide-react";
import React, { useMemo } from "react";
import { Avatar, Button, Radio, Typography } from "antd";
import DashboardCharts from "./DashboardCharts";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import type { AppDispatch } from "../redux/store";
import { resetForStudySwitch, clearSession, setSessionFetched, setSessionLoading } from "../redux/testDesignSlice";
import axiosInstance from "../utils/axiosInstance";
import mixpanel from "mixpanel-browser";
import { Events } from "../constants/Event";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ReactECharts from "echarts-for-react";

dayjs.extend(utc);

const { Title } = Typography;

interface GeoTest {
  title: string;
  subtitle?: string;
  date: string;
  status?: "in_progress" | "error" | "completed";
  action?: "VIEW_RESULTS" | "VIEW_CONTROLS";
  jobConfigId?: string;
  buyType?: string;
  isSales?: boolean;
  modelParameterConfig?: any;
  modelOutputConfig?: { outputData?: any };
}

interface SalesData {
  withoutCampaign: number;
  withCampaign: number;
  adSpend: number;
  incrementalSales: number;
  jobConfigId: any;
}

interface JobRun {
  jobRunId: string;
  tenantId: string;
  jobConfigId: string;
  jobType: string;
  jobStatus: string;
  jobRunCount: number;
  jobRunTimeout: number;
  jobExecutionTime: string;
  createdDate: string;
  modifiedDate: string;
}

interface GeoBasedIncrementilityProps {
  geoTests: GeoTest[];
  salesData?: SalesData;
  setSelectedJobIndex: (index: number) => void;
  totalLiftPercentage?: number;
  totalLiftPercentageLowerBound?: number;
  totalLiftPercentageUpperBound?: number;
  totalROASLowerBound?: number;
  totalROASUpperBound?: number;
  selectedJobIndex: number;
  jobRuns: JobRun[];
}

const GeoBasedIncrementility: React.FC<GeoBasedIncrementilityProps> = ({
  geoTests,
  salesData,
  totalLiftPercentage,
  totalLiftPercentageLowerBound,
  totalLiftPercentageUpperBound,
  totalROASLowerBound,
  totalROASUpperBound,
  jobRuns,
  setSelectedJobIndex,
  selectedJobIndex,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const handleViewResults = (test: GeoTest) => {
    if (test.jobConfigId) {
      const encodedName = encodeURIComponent(test.title);
      dispatch(resetForStudySwitch());
      navigate(
        `/measurement/geo-lift/${test.jobConfigId}?name=${encodedName}`,
        {
          state: {
            jobName: test.title,
            buyType: test?.buyType,
            modelParameterConfig: test?.modelParameterConfig,
          },
        }
      );
    }
  };
  const onExecuteClick = async (jobConfigId: string) => {
    await axiosInstance.post(
      "/api/jobRun",
      {
        jobConfigId,
      },
      {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("authToken"),
        },
      }
    );
    mixpanel.track(Events.BUTTON_CLICK, {
      jobType: "GEO_LIFT",
      action: "Execute",
      jobConfigId,
    });
    window.location.reload();
  };

  const getJobStatus = (jobConfigId: string) => {
    const jobRun = jobRuns.find((run) => run.jobConfigId === jobConfigId);
    return jobRun;
  };

  // Helper function to check if a job is stuck (same logic as synthetic control list)
  const getActualJobStatus = (jobRun: JobRun | undefined): string => {
    if (!jobRun) return "UNKNOWN";

    const { jobStatus, jobExecutionTime } = jobRun;
    const isStuck =
      (jobStatus === "RUNNING" || jobStatus === "PENDING") &&
      dayjs.utc().diff(dayjs.utc(jobExecutionTime), "hour") >= 2;

    return isStuck ? "FAILED" : jobStatus;
  };

  const renderActionButton = (test: GeoTest) => {
    const jobRun = getJobStatus(test.jobConfigId || "");
    const actualStatus = getActualJobStatus(jobRun);

    if (actualStatus === "RUNNING") {
      return (
        <button className="text-white text-xs px-3 py-1 rounded w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 whitespace-nowrap">
          IN PROGRESS
        </button>
      );
    } else if (actualStatus === "FAILED") {
      return (
        <button
          onClick={() => onExecuteClick(test.jobConfigId || "")}
          className="text-white text-xs px-3 py-1 rounded w-full sm:w-auto bg-red-500 hover:bg-red-600 whitespace-nowrap"
        >
          FAILED
        </button>
      );
    } else if (!jobRun || actualStatus === "UNKNOWN") {
      return (
        <button
          onClick={() => onExecuteClick(test.jobConfigId || "")}
          className="text-white text-xs px-3 py-1 rounded w-full sm:w-auto bg-green-500 hover:bg-green-600 whitespace-nowrap"
        >
          Execute
        </button>
      );
    } else {
      return (
        <button
          onClick={() => handleViewResults(test)}
          className="flex items-center gap-1 text-white text-xs px-3 py-1 rounded bg-[#0055B8] hover:bg-[#003D82] whitespace-nowrap flex-shrink-0"
        >
          <Eye className="w-3 h-3" />
          View Results
        </button>
      );
    }
  };

  const proccessedGeoLiftJob = useMemo(() => {
    return geoTests.find((ele) => ele.jobConfigId === salesData?.jobConfigId);
  }, [geoTests, salesData]);

  const dollarPrefix = proccessedGeoLiftJob?.isSales ? "$ " : "";

  return (
    <div className="">
      <div className=" flex justify-center">
        <div className="bg-gradient-to-r from-indigo-50 via-white to-indigo-50 rounded-2xl shadow-lg p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 w-full border border-indigo-100 animate-fadeIn">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <div className="p-3 bg-indigo-100 rounded-full">
                <FlaskConical className="h-8 w-8 text-indigo-600" />
              </div>
            </div>
            <div>
              <Title level={1} className="m-0">
                Incrementality Results
              </Title>
              <p className=" mt-[-10px] text-lg text-gray-600">
                {proccessedGeoLiftJob?.title || "No job title available"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                dispatch(clearSession());
                dispatch(setSessionFetched(false));
                dispatch(setSessionLoading(false));
                navigate(`/measurement/geo-lift/${uuidv4()}?mode=evaluate`);
              }}
              className="px-4 py-2 bg-[#0055B8] text-white rounded-lg shadow hover:bg-[#003D82] transition"
            >
              Create a new job
            </button>
            <button
              onClick={() => navigate("/measurement/synthetic_control_list")}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg shadow hover:bg-gray-200 transition"
            >
              View All
            </button>
          </div>
        </div>
      </div>
      <div className=" pt-4  grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 ">
        <div className="col-span-1 md:col-span-4 bg-white p-4 rounded-xl shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-700">
              Historical Synthetic Control Results
            </h3>
          </div>
          <div className="space-y-4">
            {geoTests.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 text-indigo-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-gray-800 mb-2">
                  No Synthetic Control studies yet
                </h4>
                <p className="text-gray-500 text-center text-sm mb-6 max-w-sm">
                  Create your first study to measure the true incremental impact
                  of your marketing campaigns using advanced causal inference.
                </p>
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 p-5 rounded-xl text-left w-full max-w-md">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg
                        className="w-3.5 h-3.5 text-indigo-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-indigo-900 font-medium text-sm mb-1.5">
                        What is Synthetic Control?
                      </p>
                      <p className="text-gray-700 text-xs leading-relaxed">
                        A rigorous method that creates a synthetic control group
                        from untreated markets to estimate the causal impact of
                        your campaigns on key business metrics.
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-indigo-100 pt-3 mt-3">
                    <p className="text-gray-600 text-xs leading-relaxed">
                      <span className="font-medium text-gray-800">
                        Get started:
                      </span>{" "}
                      Upload your market-level data, select test and control
                      regions, and let our platform analyze the incremental lift
                      from your marketing efforts.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/measurement/geo-lift")}
                  className="mt-6 px-6 py-2.5 bg-[#0055B8] text-white text-sm font-medium rounded-lg shadow-sm hover:bg-[#003D82] hover:shadow-md transition-all duration-200"
                >
                  Create Your First Study
                </button>
              </div>
            ) : (
              geoTests
                .map((test, originalIndex) => ({ test, originalIndex }))
                .filter(({ test }) => {
                  const jobRun = getJobStatus(test.jobConfigId || "");
                  const actualStatus = getActualJobStatus(jobRun);
                  return actualStatus === "SUCCESSFUL" && !!test.modelOutputConfig?.outputData;
                })
                .slice(0, 5)
                .map(({ test, originalIndex }) => {
                const jobRun = getJobStatus(test.jobConfigId || "");
                const actualStatus = getActualJobStatus(jobRun);
                const showRadio = actualStatus === "SUCCESSFUL";

                return (
                  <div
                    key={originalIndex}
                    onClick={() => showRadio && setSelectedJobIndex(originalIndex)}
                    className="flex items-start sm:items-center border-b pb-3 gap-3 cursor-pointer"
                  >
                    {/* ✅ LEFT SIDE RADIO */}
                    <div className="pt-1">
                      {showRadio && (
                        <Radio checked={selectedJobIndex === originalIndex} />
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 w-full">
                      <div>
                        {test.subtitle && (
                          <p className="text-sm text-gray-600">
                            {test.subtitle}
                          </p>
                        )}
                        <p className="text-sm font-medium">{test.title}</p>
                        <div className="flex items-center text-xs text-gray-400 mt-1">
                          <CalendarIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span>{test.date}</span>
                        </div>
                      </div>

                      {renderActionButton(test)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="col-span-1 md:col-span-8 bg-white p-4 rounded-xl shadow-md">
          {!salesData ||
          (!salesData.withCampaign && !salesData.withoutCampaign) ? (
            <div className="text-center py-8 h-[500px] flex justify-center items-center">
              <div className="">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">
                  No Data
                </h3>
                <p className="text-gray-600 mb-6">
                  Check incremental impact of synthetic control Test here
                </p>
                <Button type="primary" className="mt-4 w-full">
                  VIEW MARKETS
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800 text-center mb-4">
                  Campaign Results
                </h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left side - Metric cards stacked vertically */}
                <div className="lg:col-span-1 flex flex-col gap-4">
                  <div className="bg-[#0055B8] p-4 rounded-lg">
                    <div className="flex flex-col items-center text-center">
                      <div className="flex items-center mb-2">
                        <TrendingUpIcon className="h-5 w-5 mr-2 text-white/80" />
                        <span className="font-medium text-white/90">
                          Total Lift
                        </span>
                      </div>
                      <div
                        className={`text-2xl font-bold ${
                          (totalLiftPercentage ?? 0) >= 0
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {(totalLiftPercentage ?? 0) >= 0 ? "+" : ""}
                        {(totalLiftPercentage ?? 0).toFixed(2)}%
                      </div>
                      <div
                  className={`text-base font-bold ${
                    (totalLiftPercentageLowerBound ?? 0) >= 0
                      ? "text-green-400"
                      : "text-red-400"
                  }`}
                >
                  {(totalLiftPercentageLowerBound ?? 0) >= 0
                    ? "+"
                    : ""}
                  {(totalLiftPercentageLowerBound ?? 0).toFixed(2)}%

                  <span> - </span>
                {(totalLiftPercentageUpperBound ?? 0) >= 0
                    ? "+"
                    : ""}
                  {(totalLiftPercentageUpperBound ?? 0).toFixed(2)}%

                </div>

                    </div>
                  </div>

                  <div className="bg-[#0055B8] p-4 rounded-lg">
                    <div className="flex flex-col items-center text-center">
                      <span className="font-medium text-blue-100 mb-2">
                        Incremental Outcome
                      </span>
                      <div className="text-2xl font-bold text-white">
                        {dollarPrefix}
                        {(salesData.incrementalSales ?? 0).toLocaleString('en-US')}
                      </div>
                    </div>
                  </div>

                  { (proccessedGeoLiftJob?.isSales === true || proccessedGeoLiftJob?.isSales === undefined) && (
                    <div className="bg-[#0055B8] p-4 rounded-lg">
                      <div className="flex flex-col items-center text-center">
                        <div className="flex items-center mb-2">
                          <DollarSignIcon className="h-5 w-5 text-white/80 mr-2" />
                          <span className="font-medium text-blue-100">iROAS</span>
                        </div>
                        <div className="text-2xl font-bold text-white">
                          {(
                            (salesData.incrementalSales ?? 0) /
                            (salesData.adSpend || 1)
                          ).toFixed(2)}
                          x
                        </div>

                        <div className="text-base font-bold text-white">
                    {(totalROASLowerBound ?? 0).toFixed(2)}x - {(totalROASUpperBound ?? 0).toFixed(2)}x
                  </div>

                      </div>
                    </div>
                  )}
                </div>

                {/* Right side - Horizontal bar chart */}
                <div className="lg:col-span-2 bg-gray-50 p-4 rounded-lg flex flex-col">
                  <h4 className="text-md font-semibold text-gray-800 mb-3 text-center">
                    Control vs Test Market Outcome
                  </h4>
                  <div className="flex-1">
                    <ReactECharts
                      option={{
                        grid: {
                          left: "15%",
                          right: "10%",
                          top: "10%",
                          bottom: "10%",
                          containLabel: true,
                        },
                        xAxis: {
                          type: "value",
                          axisLabel: {
                            formatter: (value: number) => {
                              if (value >= 1_000_000) {
                                return (value / 1_000_000).toFixed(1) + "M";
                              } else if (value >= 1_000) {
                                return (value / 1_000).toFixed(1) + "K";
                              }
                              return value.toLocaleString();
                            },
                            fontSize: 12,
                            color: "#4B5563",
                          },
                          splitLine: {
                            lineStyle: {
                              color: "#E5E7EB",
                            },
                          },
                        },
                        yAxis: {
                          type: "category",
                          data: ["Control", "Test"],
                          axisLabel: {
                            fontSize: 14,
                            color: "#1F2937",
                            fontWeight: 500,
                          },
                          axisLine: {
                            show: false,
                          },
                          axisTick: {
                            show: false,
                          },
                        },
                        series: [
                          {
                            type: "bar",
                            data: [
                              {
                                value: Math.round(
                                  salesData.withoutCampaign || 0
                                ),
                                itemStyle: { color: "#6B7280" },
                              },
                              {
                                value: Math.round(salesData.withCampaign || 0),
                                itemStyle: { color: "#0055B8" },
                              },
                            ],
                            barWidth: "50%",
                            label: {
                              show: true,
                              position: "right",
                              formatter: (params: any) => {
                                const value = params.value;
                                if (value >= 1_000_000) {
                                  return `${dollarPrefix}${(value / 1_000_000).toFixed(1)}M`;
                                } else if (value >= 1_000) {
                                  return `${dollarPrefix}${(value / 1_000).toFixed(1)}K`;
                                }
                                return `${dollarPrefix}${value.toLocaleString()}`;
                              },
                              fontSize: 12,
                              fontWeight: "bold",
                              color: "#1F2937",
                            },
                          },
                        ],
                        tooltip: {
                          trigger: "axis",
                          axisPointer: {
                            type: "shadow",
                          },
                          formatter: (params: any) => {
                            const param = params[0];
                            return `${
                              param.name
                            }: ${dollarPrefix}${param.value.toLocaleString()}`;
                          },
                        },
                      }}
                      style={{ height: "100%", minHeight: "250px" }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeoBasedIncrementility;
